import { System } from "ecsy";
import { createEntity, CreationRequest, destroyEntity, getEntityById } from "../../../shared/entityFactory";
import { entityTrace, printSyncTrace } from "../../../shared/entityTrace";
import { GameElement } from "../../../shared/gameElementTypes";
import { CollisionReceipt, CreationReceipt, EventReceipt, MoveTriggerReceipt, SyncEntityState, SyncMessageType, TargetChangeReceipt } from "../../../shared/messageTypes";
import { deserializeEntityState } from "../../../shared/synchronization";
import { ChainSystem } from "../../../shared/systems/chainSystem";
import { MovementSystem } from "../../../shared/systems/movementSystem";
import { PhysicsSystem } from "../../../shared/systems/physicsSystem";
import { assertClient, getSystem } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { MessageBuffer } from "../messageBuffer";
import { ClientTargetingSystem } from "./clientTargetingSystem";

assertClient();

export class ClientSyncSystem extends System {
	stepNumber = 0;
	currentState = "Lobby";
	messageBuffer: MessageBuffer;
	maximumStep = 0; //-1 for not currently known maximum step
	game: GameClient;
	physicsSystem!: PhysicsSystem
	targetingSystem!: ClientTargetingSystem
	movementSystem!: MovementSystem


	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.messageBuffer = attributes.messageBuffer;
		this.game = attributes.game;
	}

	init() {
		window.getStepNumber = () => {
			return this.stepNumber;
		}
	}

	execute(delta: number) {
		if (this.maximumStep < 0 || this.stepNumber <= this.maximumStep) {
			++this.stepNumber;
		}

		if (!this.physicsSystem) {//RegisterSystem -> init() apparently called before physics system added??
			this.physicsSystem = getSystem(this.world, PhysicsSystem);
		}
		if (!this.targetingSystem) {//RegisterSystem -> init() apparently called before physics system added??
			this.targetingSystem = getSystem(this.world, ClientTargetingSystem);
		}
		if (!this.movementSystem) {
			this.movementSystem = getSystem(this.world, MovementSystem);
		}

		//Sync chains, happens once at start of sim
		const chainSync = this.messageBuffer.dequeueMessage(SyncMessageType.ChainSync, this.stepNumber)
		if (chainSync) {
			const connections = JSON.parse(chainSync.connections);
			getSystem(this.game.world, ChainSystem).persistedLinks = new Map(connections);
		}

		//Sync to previous frames states
		const stateMsg = this.messageBuffer.dequeueMessage(SyncMessageType.State, this.stepNumber);
		if (stateMsg) {
			this.doStateSync(stateMsg.payload);
		}

		//Handle create/destroy events from this step
		do {
			var eventMsg = this.messageBuffer.dequeueMessage(SyncMessageType.Event, this.stepNumber)
			if (eventMsg) {
				if (eventMsg.stepNumber !== this.stepNumber) {
					console.warn(`Buffering check: Received event message for step ${eventMsg.stepNumber} when current step is ${this.stepNumber}`);
				}

				for (const event of eventMsg.payload) {
					this.enactEvent(event);
				}
			}
		} while (eventMsg)

		//Process end step
		this.messageBuffer.dequeueMessage(SyncMessageType.StepEnd, this.stepNumber);
	}

	doStateSync(state: SyncEntityState[]) {
		for (const entityState of state) {
			const entity = getEntityById(entityState.id);
			if (!entity) return; //throw new Error(`StateSync: Received state for non-existant entity, should be impossible, StepNumber: ${this.stepNumber}`);
			entityTrace(entity, `syncing physics state`);
			deserializeEntityState(entity, entityState);
		}
	}

	enactEvent(event: EventReceipt) {
		if (isCreateEvent(event)) {
			//console.log(`received create event ${event.id}, (${GameElement[event.type]}) step ${getStepNumber()}`);
			let params: CreationRequest = event;
			params.sourceCompElement = this.game.getCompElementById(event.id) || undefined;
			createEntity(params);
		}
		else if (isCollisionEvent(event)) {
			//console.log(`(${getStepNumber()}) Receving collision event ${event.id} ${event.hitId}`);
			this.physicsSystem.causeCollision(event.id, event.hitId, event.canDamage);
		}
		else if (isTargetChangeEvent(event)) { //should this one be on stepnumber - 1 due to targeting running after physics? what if entity targets and is created at same time
			//console.log(`Received targetChange: Step: ${this.stepNumber} event id ${event.id}`);
			this.targetingSystem.changeEvents.push(event);
		}
		else if (isMoveTriggerEvent(event)) {
			this.movementSystem.moveTriggerQueue.push(event);
		}
		else {
			//console.log(`received destroy event ${event.id}, (${GameElement[event.type]}) step ${getStepNumber()}`);
			const entity = getEntityById(event.id);
			if (!entity) {
				const err = new Error(`StateSync: Received destroyEvent for non-existant entity, should be impossible: step=${this.stepNumber}, id=${event.id} type=${GameElement[event.type]}`);
				printSyncTrace(event.id);
				throw err;
			}
			entityTrace(entity, 'destroying entity');

			destroyEntity(entity, true);
		}
	}

	getStepNumber() {
		return this.stepNumber;
	}

	resetSteps() {
		this.stepNumber = -1; //will increment to 0 on first execute
	}
}

function isCreateEvent(event: EventReceipt): event is CreationReceipt {
	return (event as CreationReceipt).position !== undefined;
}

function isCollisionEvent(event: EventReceipt): event is CollisionReceipt {
	return (event as CollisionReceipt).hitId !== undefined;
}

function isTargetChangeEvent(event: EventReceipt): event is TargetChangeReceipt {
	return (event as TargetChangeReceipt).specIndex !== undefined;
}

function isMoveTriggerEvent(event: EventReceipt): event is MoveTriggerReceipt {
	return (event as MoveTriggerReceipt).isMoveTrigger === true;
}
